import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/staffApi";

const input = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm";
const CATS = [["billing", "Billing & refunds"], ["account", "Account"], ["runs", "Agent runs"], ["teams", "Teams"], ["privacy", "Privacy & data"], ["bug", "Something's broken"], ["other", "Other"]];
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

/* Help: support tickets (members see staff replies, never internal notes) and organisation verification. */
export default function Help() {
  const { user, loading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ subject: "", category: "billing", message: "" });
  const [reply, setReply] = useState({});
  const [verify, setVerify] = useState([]);
  const [note, setNote] = useState("");
  const [m, setM] = useState(null);
  const load = useCallback(() => {
    api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/verification/requests").then((r) => setVerify(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const run = async (fn, ok) => { setM(null); try { await fn(); setM({ text: ok }); load(); } catch (e) { setM({ err: 1, text: errMsg(e) }); } };

  if (loading) return null;
  if (!user) return <div className="mx-auto mt-16 max-w-md text-center text-sm">Please <Link className="underline" to="/login">sign in</Link> to contact support.</div>;
  const pendingVerify = verify.some((v) => v.status === "pending");

  return (
    <div className="mx-auto max-w-2xl px-4 py-8" data-testid="help-page">
      <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Settings</Link>
      <h1 className="mt-3 font-display text-2xl font-semibold">Support</h1>
      {m && <p className={`mt-2 text-sm ${m.err ? "text-red-600" : "text-emerald-700"}`}>{m.text}</p>}

      <section className="mt-6 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-sm font-semibold">Contact support</h2>
        <input className={`${input} mt-3`} placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="help-subject" />
        <select className={`${input} mt-2`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
          {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <textarea className={`${input} mt-2`} rows={4} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="help-message" />
        <Button className="mt-2" disabled={form.subject.trim().length < 3 || form.message.trim().length < 5}
          onClick={() => run(async () => { await api.post("/support/tickets", form); setForm({ subject: "", category: "billing", message: "" }); }, "Sent. We'll email you when we reply.")}
          data-testid="help-send">Send</Button>
      </section>

      {tickets.map((t) => (
        <section key={t.id} className="mt-4 rounded-2xl border border-border bg-card p-4 text-sm">
          <div className="font-semibold">{t.subject} <span className="text-xs font-normal text-muted-foreground">· {t.status}</span></div>
          {t.messages.map((msg, i) => (
            <div key={i} className={`mt-2 rounded p-2 ${msg.from === "staff" ? "bg-muted" : ""}`}>
              <div className="text-xs text-muted-foreground">{msg.from === "staff" ? "Nimbus support" : "You"} · {when(msg.at)}</div>
              <div className="whitespace-pre-wrap">{msg.body}</div>
            </div>
          ))}
          {t.status !== "closed" && (
            <div className="mt-2 flex gap-2">
              <input className={input} value={reply[t.id] || ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })} placeholder="Reply" />
              <Button size="sm" disabled={!(reply[t.id] || "").trim()} onClick={() => run(async () => { await api.post(`/support/tickets/${t.id}/reply`, { message: reply[t.id] }); setReply({ ...reply, [t.id]: "" }); }, "Reply sent.")}>Send</Button>
            </div>
          )}
        </section>
      ))}

      <section className="mt-6 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-sm font-semibold">Verify your organisation</h2>
        <p className="mt-1 text-xs text-muted-foreground">Tell us your company's legal name and GSTIN (or another registration number). We check it by hand.</p>
        {verify.map((v) => (
          <p key={v.id} className="mt-2 text-xs">Request of {when(v.created_at)}: <b>{v.status}</b>{v.decision_reason && v.status === "rejected" ? ` (${v.decision_reason})` : ""}</p>
        ))}
        {!pendingVerify && (
          <>
            <textarea className={`${input} mt-2`} rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Acme Analytics Pvt Ltd, GSTIN …" />
            <Button className="mt-2" size="sm" disabled={note.trim().length < 10}
              onClick={() => run(async () => { await api.post("/verification/requests", { kind: "organization", note }); setNote(""); }, "Request sent.")}>Request verification</Button>
          </>
        )}
      </section>
    </div>
  );
}
